/**
 * RespectIcon.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Senior implementation: state-driven, one-shot SVG path morph
 * RESPECT → MUTUAL_RESPECT triggers morph ONCE, never replays.
 *
 * State machine:
 *   INACTIEF        → grey split-loops, no animation
 *   RESPECT         → left loop lit (silver/sage), right grey
 *   MUTUAL_RESPECT  → both loops lit
 *                     IF arrived via RESPECT→MUTUAL transition: morph plays once
 *                     IF loaded directly into MUTUAL (refresh/hydration): static, no morph
 *
 * Anti-regression guarantees:
 *   - useRef tracks previousState WITHOUT triggering re-renders
 *   - shouldAnimate flag lives in a ref, never in state
 *   - animationId ref prevents double-fire on StrictMode double-mount
 *   - SVG path morph via SMIL animateTransform is avoided (compat issues)
 *     → Uses CSS transition on stroke-dasharray + clip-path reveal instead
 *   - No Framer Motion dependency (zero risk of version conflicts)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, {
  useEffect,
  useRef,
  useCallback,
  memo,
} from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type RespectState = "INACTIEF" | "RESPECT" | "MUTUAL_RESPECT";

interface RespectIconProps {
  state: RespectState;
  /** Size in px — icon scales proportionally from this width */
  size?: number;
  className?: string;
}

// ─── SVG Path constants ───────────────────────────────────────────────────────

// Viewbox: 0 0 300 100
// Left loop:  starts and ends at crossing point (150,37)
const PATH_LEFT =
  "M 150 37 C 144 20, 110 6, 72 6 C 34 6, 6 18, 6 37 C 6 56, 34 68, 72 68 C 110 68, 144 54, 150 37";

// Right loop: starts and ends at crossing point (150,37)
const PATH_RIGHT =
  "M 150 37 C 156 20, 190 6, 228 6 C 266 6, 294 18, 294 37 C 294 56, 266 68, 228 68 C 190 68, 156 54, 150 37";

// Highlight arcs (top edge only — gives 3D ribbon feel)
const PATH_HL_LEFT  = "M 150 37 C 145 22, 112 7, 76 7 C 42 7, 8 18, 6 37";
const PATH_HL_RIGHT = "M 150 37 C 155 22, 188 7, 224 7 C 258 7, 292 18, 294 37";

// Approximate perimeter of one loop (for stroke-dasharray shimmer)
const LOOP_PERIMETER = 516;

// ─── Color tokens ─────────────────────────────────────────────────────────────

const COLOR = {
  inactive: "#CDD0D8",
  depthStart: "#1a1a1a",
  depthMid1:  "#747474",
  depthMid2:  "#e2e2e2",
  depthMid3:  "#c4c4c4",
  depthEnd:   "#505050",
  sageStart:  "#3D6B58",
  sageMid:    "#9DC4B4",
  sageEnd:    "#3D6B58",
  dot:        "#9DC4B4",
} as const;

// ─── Animation durations (ms) ─────────────────────────────────────────────────

const MORPH_DURATION_MS = 680;
const SHIMMER_DURATION_S = 9;

// ─── Utility: stable unique ID per component instance ────────────────────────

let _idCounter = 0;
function useStableId(prefix: string): string {
  const ref = useRef<string | null>(null);
  if (ref.current === null) {
    ref.current = `${prefix}-${++_idCounter}`;
  }
  return ref.current;
}

// ─── InfinityLoop: renders ONE loop with full layering ────────────────────────

interface LoopProps {
  path: string;
  hlPath: string;
  gradientId: string;   // depth gradient
  sageId: string;       // sage overlay gradient
  maskId: string;       // reflection mask
  shimmerOffset: number; // stroke-dashoffset start (0 for left, -258 for right)
  /** 0–1. Controls opacity of ALL active layers */
  intensity: number;
  /** Whether to animate shimmer */
  shimmer: boolean;
  /** Whether reflection is visible */
  reflection: boolean;
  /** Flip x gradient (right loop reads right→left) */
  flipGradient?: boolean;
}

// Separated to avoid re-declaring gradient defs on every render
const LoopDefs = memo(function LoopDefs({
  gradientId,
  sageId,
  maskId,
  flipGradient,
}: Pick<LoopProps, "gradientId" | "sageId" | "maskId" | "flipGradient">) {
  const x1 = flipGradient ? "100%" : "0%";
  const x2 = flipGradient ? "0%" : "100%";
  return (
    <defs>
      <linearGradient id={gradientId} x1={x1} y1="0%" x2={x2} y2="0%">
        <stop offset="0%"   stopColor={COLOR.depthStart} />
        <stop offset="14%"  stopColor={COLOR.depthMid1}  />
        <stop offset="36%"  stopColor={COLOR.depthMid2}  />
        <stop offset="56%"  stopColor={COLOR.depthMid3}  />
        <stop offset="100%" stopColor={COLOR.depthEnd}   />
      </linearGradient>

      <linearGradient id={sageId} x1={x1} y1="0%" x2={x2} y2="0%">
        <stop offset="0%"   stopColor={COLOR.sageStart} stopOpacity={0}    />
        <stop offset="55%"  stopColor={COLOR.sageMid}   stopOpacity={0.38} />
        <stop offset="100%" stopColor={COLOR.sageEnd}   stopOpacity={0.08} />
      </linearGradient>

      <linearGradient id={`${maskId}-grad`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%"   stopColor="white" stopOpacity={0.28} />
        <stop offset="100%" stopColor="white" stopOpacity={0}    />
      </linearGradient>
      <mask id={maskId}>
        <rect x="0" y="50" width="300" height="50" fill={`url(#${maskId}-grad)`} />
      </mask>
    </defs>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

const RespectIcon = memo(function RespectIcon({
  state,
  size = 52,
  className,
}: RespectIconProps) {
  // ── Stable IDs (survives re-renders, unique per instance) ──────────────────
  const uid         = useStableId("ri");
  const leftDepthId  = `${uid}-ld`;
  const leftSageId   = `${uid}-ls`;
  const leftMaskId   = `${uid}-lm`;
  const rightDepthId = `${uid}-rd`;
  const rightSageId  = `${uid}-rs`;
  const rightMaskId  = `${uid}-rm`;
  const glowId       = `${uid}-glow`;

  // ── Transition detection (never causes re-render) ─────────────────────────
  const prevStateRef        = useRef<RespectState | null>(null);
  const shouldMorphRef      = useRef(false);   // morph flag (transient)
  const animationTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef        = useRef(false);

  // ── DOM refs for imperative animation (avoids state churn) ───────────────
  const rightLoopGroupRef   = useRef<SVGGElement>(null);
  const morphOverlayRef     = useRef<SVGGElement>(null);

  // ── Derived display values (pure, no state) ───────────────────────────────
  const isInactief = state === "INACTIEF";
  const isRespect  = state === "RESPECT";
  const isMutual   = state === "MUTUAL_RESPECT";

  const leftActive  = isRespect || isMutual;
  const rightActive = isMutual;

  // ── Transition detection via useEffect ───────────────────────────────────
  useEffect(() => {
    const prev = prevStateRef.current;
    const curr = state;

    // Detect the ONLY transition that triggers morph
    const isRespectToMutual =
      prev === "RESPECT" && curr === "MUTUAL_RESPECT";

    // CRITICAL: Only animate on actual user-driven transition.
    // If component mounts directly into MUTUAL (refresh/hydration/deeplink),
    // prev is null → condition is false → no morph. Safe.
    if (isRespectToMutual && isMountedRef.current) {
      shouldMorphRef.current = true;
      triggerMorph();
    }

    prevStateRef.current = curr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Mark as mounted AFTER first render (prevents StrictMode double-fire)
  useEffect(() => {
    // In StrictMode, effects run twice. We set mounted on the SECOND run.
    // The morph detection fires in a separate effect (state dep), so by the
    // time it could fire, isMountedRef is already true — safe.
    const t = setTimeout(() => { isMountedRef.current = true; }, 0);
    return () => {
      clearTimeout(t);
      isMountedRef.current = false;
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    };
  }, []);

  // ── Imperative morph: reveal right loop once, then settle ─────────────────
  const triggerMorph = useCallback(() => {
    const rightGroup    = rightLoopGroupRef.current;
    const morphOverlay  = morphOverlayRef.current;
    if (!rightGroup || !morphOverlay) return;

    // Clear any pending timers (safety)
    if (animationTimerRef.current) clearTimeout(animationTimerRef.current);

    // Step 1: Right loop starts invisible (clip reveals left→right)
    rightGroup.style.opacity         = "0";
    rightGroup.style.clipPath        = "inset(0 100% 0 0)";
    rightGroup.style.transition      = "none";

    // Morph overlay (brief glow pulse at crossing point)
    morphOverlay.style.opacity       = "0";
    morphOverlay.style.transition    = "none";

    // Step 2: On next frame, begin transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Reveal right loop via clip-path (right loop "grows" from center outward)
        rightGroup.style.transition  =
          `opacity ${MORPH_DURATION_MS * 0.3}ms ease,
           clip-path ${MORPH_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        rightGroup.style.opacity     = "1";
        rightGroup.style.clipPath    = "inset(0 0% 0 0)";

        // Brief glow pulse at crossing point during morph
        morphOverlay.style.transition = `opacity ${MORPH_DURATION_MS * 0.4}ms ease`;
        morphOverlay.style.opacity    = "1";

        // Fade glow after peak
        animationTimerRef.current = setTimeout(() => {
          morphOverlay.style.transition = `opacity ${MORPH_DURATION_MS * 0.6}ms ease`;
          morphOverlay.style.opacity    = "0";

          // Reset flag — animation complete, will never replay
          animationTimerRef.current = setTimeout(() => {
            shouldMorphRef.current = false;
          }, MORPH_DURATION_MS * 0.6);
        }, MORPH_DURATION_MS * 0.5);
      });
    });
  }, []);

  // ── Right loop initial style (static, set once) ───────────────────────────
  // If we mount directly into MUTUAL: opacity 1, no clip (fully visible, static)
  // If we are mid-morph: triggerMorph handles it imperatively
  const rightLoopInitialStyle: React.CSSProperties = {
    opacity:  rightActive ? 1 : 0,
    clipPath: rightActive ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
    transition: "opacity 0.35s ease, clip-path 0.35s ease",
  };

  // ── Dimensions ────────────────────────────────────────────────────────────
  const height = Math.round(size * (100 / 300));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 300 100"
      overflow="visible"
      className={className}
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      {/* ── Gradient + mask defs ── */}
      <defs>
        {/* Left depth */}
        <linearGradient id={leftDepthId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={COLOR.depthStart} />
          <stop offset="14%"  stopColor={COLOR.depthMid1}  />
          <stop offset="36%"  stopColor={COLOR.depthMid2}  />
          <stop offset="56%"  stopColor={COLOR.depthMid3}  />
          <stop offset="100%" stopColor={COLOR.depthEnd}   />
        </linearGradient>
        {/* Left sage */}
        <linearGradient id={leftSageId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={COLOR.sageStart} stopOpacity={0}    />
          <stop offset="55%"  stopColor={COLOR.sageMid}   stopOpacity={0.38} />
          <stop offset="100%" stopColor={COLOR.sageEnd}   stopOpacity={0.08} />
        </linearGradient>
        {/* Left reflection mask */}
        <linearGradient id={`${leftMaskId}-g`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="white" stopOpacity={0.28} />
          <stop offset="100%" stopColor="white" stopOpacity={0}    />
        </linearGradient>
        <mask id={leftMaskId}>
          <rect x="0" y="50" width="300" height="50" fill={`url(#${leftMaskId}-g)`} />
        </mask>

        {/* Right depth (flipped) */}
        <linearGradient id={rightDepthId} x1="100%" y1="0%" x2="0%" y2="0%">
          <stop offset="0%"   stopColor={COLOR.depthStart} />
          <stop offset="14%"  stopColor={COLOR.depthMid1}  />
          <stop offset="36%"  stopColor={COLOR.depthMid2}  />
          <stop offset="56%"  stopColor={COLOR.depthMid3}  />
          <stop offset="100%" stopColor={COLOR.depthEnd}   />
        </linearGradient>
        {/* Right sage (flipped) */}
        <linearGradient id={rightSageId} x1="100%" y1="0%" x2="0%" y2="0%">
          <stop offset="0%"   stopColor={COLOR.sageStart} stopOpacity={0}    />
          <stop offset="55%"  stopColor={COLOR.sageMid}   stopOpacity={0.38} />
          <stop offset="100%" stopColor={COLOR.sageEnd}   stopOpacity={0.08} />
        </linearGradient>
        {/* Right reflection mask */}
        <linearGradient id={`${rightMaskId}-g`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="white" stopOpacity={0.28} />
          <stop offset="100%" stopColor="white" stopOpacity={0}    />
        </linearGradient>
        <mask id={rightMaskId}>
          <rect x="0" y="50" width="300" height="50" fill={`url(#${rightMaskId}-g)`} />
        </mask>

        {/* Glow filter for morph pulse */}
        <filter id={glowId} x="-15%" y="-60%" width="130%" height="220%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* LEFT LOOP                                                            */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <g style={{ opacity: 1 }}>
        {/* Shadow */}
        <path
          d={PATH_LEFT}
          fill="none"
          stroke="#111"
          strokeWidth={2}
          opacity={leftActive ? 0.08 : 0}
          transform="translate(0.4, 1.4)"
          style={{ transition: "opacity 0.35s ease" }}
        />
        {/* Base stroke */}
        <path
          d={PATH_LEFT}
          fill="none"
          stroke={leftActive ? `url(#${leftDepthId})` : COLOR.inactive}
          strokeWidth={2}
          strokeLinecap="round"
          style={{ transition: "stroke 0.35s ease" }}
        />
        {/* Sage overlay */}
        <path
          d={PATH_LEFT}
          fill="none"
          stroke={`url(#${leftSageId})`}
          strokeWidth={2}
          opacity={leftActive ? 1 : 0}
          style={{ transition: "opacity 0.35s ease" }}
        />
        {/* Highlight */}
        <path
          d={PATH_HL_LEFT}
          fill="none"
          stroke="white"
          strokeWidth={0.9}
          strokeLinecap="round"
          opacity={leftActive ? 0.6 : 0}
          style={{ transition: "opacity 0.35s ease" }}
        />
        {/* Shimmer — always rendered, visibility controlled by opacity */}
        {leftActive && (
          <path
            d={PATH_LEFT}
            fill="none"
            stroke="white"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeDasharray={`16 ${LOOP_PERIMETER}`}
            opacity={0.62}
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to={`-${LOOP_PERIMETER + 16}`}
              dur={`${SHIMMER_DURATION_S}s`}
              repeatCount="indefinite"
            />
          </path>
        )}

        {/* Reflection (left, below mirror line) */}
        {leftActive && (
          <g mask={`url(#${leftMaskId})`} transform="translate(0, 136) scale(1, -1)">
            <path d={PATH_LEFT} fill="none" stroke={`url(#${leftDepthId})`} strokeWidth={2} strokeLinecap="round" />
            <path d={PATH_LEFT} fill="none" stroke={`url(#${leftSageId})`}  strokeWidth={2} />
          </g>
        )}
      </g>

      {/* Mirror line — subtle, only when at least one loop active */}
      <line
        x1="6" y1="68" x2="294" y2="68"
        stroke="#c0c4cc"
        strokeWidth={0.35}
        opacity={leftActive ? 0.4 : 0}
        style={{ transition: "opacity 0.4s ease" }}
      />

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* RIGHT LOOP                                                           */}
      {/* Controlled imperatively by triggerMorph() for the transition        */}
      {/* For static states (direct load into MUTUAL), style is set above     */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <g
        ref={rightLoopGroupRef}
        style={rightLoopInitialStyle}
      >
        {/* Shadow */}
        <path
          d={PATH_RIGHT}
          fill="none"
          stroke="#111"
          strokeWidth={2}
          opacity={0.08}
          transform="translate(0.4, 1.4)"
        />
        {/* Base stroke */}
        <path
          d={PATH_RIGHT}
          fill="none"
          stroke={`url(#${rightDepthId})`}
          strokeWidth={2}
          strokeLinecap="round"
        />
        {/* Sage overlay */}
        <path
          d={PATH_RIGHT}
          fill="none"
          stroke={`url(#${rightSageId})`}
          strokeWidth={2}
        />
        {/* Highlight */}
        <path
          d={PATH_HL_RIGHT}
          fill="none"
          stroke="white"
          strokeWidth={0.9}
          strokeLinecap="round"
          opacity={0.6}
        />
        {/* Shimmer — offset so out of phase with left */}
        <path
          d={PATH_RIGHT}
          fill="none"
          stroke="white"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeDasharray={`16 ${LOOP_PERIMETER}`}
          strokeDashoffset={-258}
          opacity={0.62}
        >
          <animate
            attributeName="stroke-dashoffset"
            from="-258"
            to={`-${258 + LOOP_PERIMETER + 16}`}
            dur={`${SHIMMER_DURATION_S}s`}
            repeatCount="indefinite"
          />
        </path>

        {/* Reflection (right) */}
        <g mask={`url(#${rightMaskId})`} transform="translate(0, 136) scale(1, -1)">
          <path d={PATH_RIGHT} fill="none" stroke={`url(#${rightDepthId})`} strokeWidth={2} strokeLinecap="round" />
          <path d={PATH_RIGHT} fill="none" stroke={`url(#${rightSageId})`}  strokeWidth={2} />
        </g>
      </g>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* MORPH OVERLAY — brief glow pulse at crossing point during transition */}
      {/* Opacity controlled imperatively, never loops                        */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <g ref={morphOverlayRef} style={{ opacity: 0 }}>
        <circle
          cx={150} cy={37} r={12}
          fill="none"
          stroke="#9DC4B4"
          strokeWidth={8}
          filter={`url(#${glowId})`}
          opacity={0.5}
        />
        <circle cx={150} cy={37} r={2.5} fill="white"   opacity={0.7} />
        <circle cx={150} cy={37} r={1.2} fill={COLOR.dot} opacity={0.95} />
      </g>

      {/* Center dot — always rendered */}
      <circle
        cx={150} cy={37} r={1.6}
        fill={leftActive ? "white" : COLOR.inactive}
        opacity={leftActive ? 0.42 : 0.5}
        style={{ transition: "fill 0.35s ease, opacity 0.35s ease" }}
      />
      {leftActive && (
        <circle cx={150} cy={37} r={0.8} fill={COLOR.dot} opacity={0.9} />
      )}
    </svg>
  );
});

export default RespectIcon;

// ─────────────────────────────────────────────────────────────────────────────
// USAGE EXAMPLE
// ─────────────────────────────────────────────────────────────────────────────
//
//  import RespectIcon, { RespectState } from "./RespectIcon";
//
//  function PostActions({ post }: { post: Post }) {
//    const state: RespectState =
//      post.mutualRespect ? "MUTUAL_RESPECT" :
//      post.respected     ? "RESPECT" :
//                           "INACTIEF";
//
//    return (
//      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
//        <RespectIcon state={state} size={52} />
//        <span>{post.respectCount}</span>
//      </div>
//    );
//  }
//
// ─────────────────────────────────────────────────────────────────────────────
// TRANSITION BEHAVIOR MATRIX
// ─────────────────────────────────────────────────────────────────────────────
//
//  From            → To              Morph fires?  Why
//  ─────────────────────────────────────────────────────
//  null (mount)    → MUTUAL_RESPECT  NO            prevState is null
//  null (mount)    → RESPECT         NO            normal mount
//  INACTIEF        → RESPECT         NO            not the target transition
//  RESPECT         → MUTUAL_RESPECT  YES ✓         the only valid trigger
//  MUTUAL_RESPECT  → RESPECT         NO            reverse, no morph
//  RESPECT         → INACTIEF        NO            unrelated
//  re-render same  → same            NO            state unchanged
//  StrictMode      → double mount    NO            isMountedRef gate
//  hydration       → MUTUAL_RESPECT  NO            prevState is null at mount
//
// ─────────────────────────────────────────────────────────────────────────────
